import React, { useState, useEffect } from 'react';
import { Download, Smartphone, X, CheckCircle2, Share } from 'lucide-react';
import { CkLogo } from './CkLogo';

export const PwaInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    // Check if already running in standalone PWA mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://');

    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    setIsIos(isIosDevice);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  if (isInstalled || isDismissed) return null;
  if (!deferredPrompt && !isIos) return null;

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  return (
    <div className="fixed bottom-20 md:bottom-6 left-4 right-4 max-w-lg mx-auto z-40 animate-in slide-in-from-bottom-5 duration-300">
      <div className="glass-card p-3.5 sm:p-4 border border-cyan-500/40 bg-[#0A0D1A]/95 shadow-2xl shadow-cyan-950/80 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <CkLogo size={38} className="shadow-glow-cyan flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-xs sm:text-sm font-bold text-white font-display flex items-center gap-1.5 truncate">
              <span>Install CK Share App</span>
              <span className="text-[9px] font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.2 rounded">
                PWA
              </span>
            </h4>
            <p className="text-[11px] text-slate-400 truncate">
              {isIos ? "Tap Share ➔ 'Add to Home Screen'" : "Add to Home Screen for 1-tap instant sync"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleInstallClick}
            className="btn-primary text-xs py-2 px-3.5 font-bold shadow-lg shadow-cyan-500/30"
          >
            {isIos ? <Share size={13} /> : <Download size={13} />}
            <span>{isIos ? 'How to Add' : 'Install App'}</span>
          </button>

          <button
            onClick={() => setIsDismissed(true)}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            title="Dismiss banner"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* iOS Step-by-Step Guide Modal */}
      {showIosGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="glass-card w-full max-w-sm p-6 text-center shadow-2xl border border-cyan-500/30">
            <CkLogo size={56} className="mx-auto mb-3" />
            <h3 className="text-base font-bold text-white font-display mb-2">Install on iOS (Safari)</h3>
            <p className="text-xs text-slate-300 mb-4 leading-relaxed">
              1. Tap the <strong>Share</strong> button (square with arrow ⎋) at the bottom of Safari.<br/>
              2. Scroll down and tap <strong>'Add to Home Screen'</strong> ➕.<br/>
              3. Tap <strong>'Add'</strong> in the top-right corner.
            </p>
            <button onClick={() => setShowIosGuide(false)} className="btn-primary w-full text-xs py-2.5">
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
